"use client";

import {
  getDoQuerySpellcheckGivenRecordOfQueryParams,
  getFilterKeysGivenRecordOfQueryParams,
  getPageNumberGivenRecordOfQueryParams,
  getRefReqIdGivenRecordOfQueryParams,
  getRefTypeGivenRecordOfQueryParams,
  getSearchQueryGivenRecordOfQueryParams,
} from "@faire/retailer-shared/search/searchRequest";
import { useSearchPageParsedSearch } from "@faire/retailer-visitor-shared/views/search-v2/SearchPageQueryParams/useSearchPageParsedSearch";
import { useQueryParams } from "@faire/web--source/ui/hooks/useQueryParams";
import { QueryParameters as SearchQueryParameters } from "@faire/web-api--source/routes/www/search";
import { QueryParameters } from "@faire/web-api--source/routes/www/search/";
import qs from "query-string";
import { useEffect, useMemo, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";

export const useSearchPageQueryParams = () => {
  "use no memo";
  const { pathname } = useLocation();
  const history = useHistory();
  const queryParams = useQueryParams(QueryParameters.parse);
  const updateQueryParamsAndStringify =
    useUpdateQueryParamsAndStringify(queryParams);
  const parsedSearch = useSearchPageParsedSearch() as SearchQueryParameters;

  return useMemo(() => {
    const searchQuery = getSearchQueryGivenRecordOfQueryParams(parsedSearch);
    const filters = getFilterKeysGivenRecordOfQueryParams(parsedSearch);
    const zeroIndexedPageNumber =
      getPageNumberGivenRecordOfQueryParams(parsedSearch);
    const referrerType = getRefTypeGivenRecordOfQueryParams(parsedSearch);
    const doQuerySpellCheck =
      getDoQuerySpellcheckGivenRecordOfQueryParams(parsedSearch);
    const refReqId = getRefReqIdGivenRecordOfQueryParams(parsedSearch);

    const setFilters = (
      updater: (oldFilters: string[]) => string[],
      clearZeroIndexedPageNumber: boolean = false
    ) => {
      const updatedFilterKeys = updater(filters);
      const queryString = updateQueryParamsAndStringify((oldParams) => ({
        ...oldParams,
        filters:
          updatedFilterKeys.length === 0
            ? undefined
            : updatedFilterKeys.join(","),
        ps: undefined,
        page: clearZeroIndexedPageNumber ? undefined : oldParams.page,
        fc: true,
      }));
      history.push({ pathname: pathname, search: queryString });
    };

    const clearFilters = () => {
      setFilters(() => []);
    };

    return {
      searchQuery,
      filters,
      setFilters,
      clearFilters,
      zeroIndexedPageNumber,
      referrerType,
      doQuerySpellCheck,
      refReqId,
    };
  }, [history, parsedSearch, pathname, updateQueryParamsAndStringify]);
};

const useUpdateQueryParamsAndStringify = (queryParams: QueryParameters) => {
  const queryParamsInMemory = useRef(queryParams);

  useEffect(() => {
    queryParamsInMemory.current = queryParams;
  }, [queryParams]);

  return useCallback(
    (updater: (oldParams: QueryParameters) => QueryParameters): string => {
      queryParamsInMemory.current = updater(queryParamsInMemory.current);
      return qs.stringify(queryParamsInMemory.current);
    },
    []
  );
};
